import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Switch,
    FormControlLabel,
    Alert,
    Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';

/**
 * OwnerAccessSettings Component
 * Dialog for toggling owner access to closeout documents
 *
 * TODO: Connect to backend PermissionsService when implemented
 * Currently this is local state only and not persisted
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {Function} onClose - Callback to close the dialog
 * @param {Function} onSuccess - Callback called on successful update
 * @param {boolean} currentValue - Current owner access status
 */
const OwnerAccessSettings = ({ open, onClose, onSuccess, currentValue }) => {
    const theme = useTheme();
    const [enabled, setEnabled] = useState(currentValue);

    // Update local state when currentValue changes
    React.useEffect(() => {
        setEnabled(currentValue);
    }, [currentValue]);

    const handleClose = () => {
        setEnabled(currentValue); // Reset to current value
        onClose();
    };

    const handleSave = () => {
        // TODO: Connect to backend PermissionsService API
        // For now, just update local state and close dialog
        if (onSuccess) {
            onSuccess(enabled);
        }
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 600 }}>Owner Access Settings</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    {/* Header Icon and Description */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mb: 3,
                            p: 2,
                            backgroundColor: enabled
                                ? theme.palette.success.lighter ||
                                  theme.palette.success.light + '20'
                                : theme.palette.grey[100],
                            borderRadius: 2,
                        }}
                    >
                        {enabled ? (
                            <LockOpenIcon
                                sx={{ fontSize: 40, color: theme.palette.success.main }}
                            />
                        ) : (
                            <LockIcon sx={{ fontSize: 40, color: theme.palette.grey[500] }} />
                        )}
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {enabled ? 'Access Enabled' : 'Access Disabled'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {enabled
                                    ? 'External owners can view closeout documents'
                                    : 'Only internal team members can view documents'}
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Toggle Switch */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={enabled}
                                onChange={(e) => setEnabled(e.target.checked)}
                                color="success"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    Grant Owner Access
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Allow external owners to view and download closeout documents
                                </Typography>
                            </Box>
                        }
                        sx={{ width: '100%', alignItems: 'flex-start', m: 0 }}
                    />

                    <Divider sx={{ my: 2 }} />

                    {/* Warning Alert - Not connected to backend */}
                    <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Preview Mode
                        </Typography>
                        <Typography variant="caption">
                            This setting is currently in preview mode and will not be saved. Backend
                            integration coming soon.
                        </Typography>
                    </Alert>

                    {/* Information Alert */}
                    <Alert severity="info" sx={{ mt: 2 }}>
                        {enabled ? (
                            <>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Notifications will be sent
                                </Typography>
                                <Typography variant="caption">
                                    External owners will receive a notification when access is
                                    granted and when new documents are uploaded.
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Restricted Access
                                </Typography>
                                <Typography variant="caption">
                                    Only Internal Admins and Project Managers will have access to
                                    closeout documents.
                                </Typography>
                            </>
                        )}
                    </Alert>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={enabled === currentValue}
                    sx={{ textTransform: 'none', minWidth: 100 }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

OwnerAccessSettings.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    currentValue: PropTypes.bool.isRequired,
};

export default OwnerAccessSettings;
